import { useState } from 'react';
import type { Product } from '../types/product';
import { PRODUCT_CATEGORIES, type ProductCategory } from '../constants/categories';

// Configure ScraperAPI via env variable; fall back gracefully when missing
const SCRAPER_API_KEY = (import.meta as any)?.env?.VITE_SCRAPER_API_KEY as string | undefined; // define in .env as VITE_SCRAPER_API_KEY
const USE_SCRAPER_API = Boolean(SCRAPER_API_KEY);

const useProductImporter = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const extractProductData = (url: string, html: string): Partial<Product> => {
    // Create a temporary DOM element to parse the HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Helper function to get meta content
    const getMeta = (name: string): string => {
      const meta = doc.querySelector(`meta[name="${name}"], meta[property="${name}"], meta[property="og:${name}"], meta[property="twitter:${name}"]`);
      return meta ? meta.getAttribute('content') || '' : '';
    };

    // Helper: try to parse JSON-LD Product schema
    const parseJsonLdProduct = () => {
      const scripts = Array.from(doc.querySelectorAll('script[type="application/ld+json"]')) as HTMLScriptElement[];
      for (const s of scripts) {
        try {
          const json = JSON.parse(s.textContent || '{}');
          const items = Array.isArray(json) ? json : (json['@graph'] || [json]);
          for (const item of items) {
            if (!item) continue;
            const type = Array.isArray(item['@type']) ? item['@type'] : [item['@type']];
            if (type && type.some((t: string) => String(t).toLowerCase() === 'product')) {
              const name = item.name || '';
              const description = item.description || '';
              const image = Array.isArray(item.image) ? item.image[0] : (item.image || '');
              const brand = typeof item.brand === 'string' ? item.brand : (item.brand?.name || undefined);
              let price = 0;
              const offers = Array.isArray(item.offers) ? item.offers[0] : item.offers;
              if (offers?.price) {
                const num = String(offers.price).replace(/[^0-9.]/g, '');
                price = parseFloat(num) || 0;
              }
              return { name, description, image, brand, price } as Partial<Product & { brand: string }>;
            }
          }
        } catch {
          // ignore malformed JSON-LD blocks
        }
      }
      return null as Partial<Product> | null;
    };

    // Check if it's an Amazon product page
    const isAmazon = url.includes('amazon.');
    
    // Extract product details
    let title = '';
    let description = '';
    let price = 0;
    let images: string[] = [];

    if (isAmazon) {
      // Handle Amazon product page
      title = doc.querySelector('#productTitle')?.textContent?.trim() || '';
      
      // Try to get price from different Amazon price elements
      const priceWhole = doc.querySelector('span.a-price-whole')?.textContent?.trim() || '0';
      const priceFraction = doc.querySelector('span.a-price-fraction')?.textContent?.trim() || '00';
      const priceText = `${priceWhole}.${priceFraction}`.replace(/[^0-9.]/g, '');
      price = parseFloat(priceText) || 0;
      
      // Get description from feature-bullets
      const descriptionBullets = Array.from(doc.querySelectorAll('#feature-bullets li'))
        .map(li => li.textContent?.trim())
        .filter(Boolean);
      description = descriptionBullets.join('\n');
      
      // Get images
      const imgElements = doc.querySelectorAll('#imgTagWrapperId img, #landingImage');
      imgElements.forEach(img => {
        const src = img.getAttribute('src') || img.getAttribute('data-old-hires');
        if (src && !src.startsWith('data:')) {
          const abs = new URL(src, url).href;
          if (!images.includes(abs)) images.push(abs);
        }
      });
    } else {
      // Handle other websites
      title = doc.querySelector('h1')?.textContent?.trim() || getMeta('title') || getMeta('og:title') || '';
      description = getMeta('description') || getMeta('og:description') || '';
      
      // Try to extract price using common selectors (allow commas as thousands separators)
      const priceMatch = html.match(/(\$|£|€|¥|₹|₩|₽|R\$|R)?\s*\d{1,3}(?:[,\s]\d{3})*(?:\.\d{1,2})?/g);
      if (priceMatch && priceMatch.length > 0) {
        const numericValue = priceMatch[0].replace(/[^0-9.]/g, '').replace(/,(?=\d{3})/g, '');
        price = parseFloat(numericValue) || 0;
      }
      
      // Get images
      const imgElements = doc.querySelectorAll('img');
      imgElements.forEach(img => {
        const src = img.getAttribute('src') || img.getAttribute('data-src') || '';
        const srcset = img.getAttribute('srcset') || img.getAttribute('data-srcset') || '';
        const pickFromSrcset = () => {
          const parts = srcset.split(',').map(s => s.trim().split(' ')[0]).filter(Boolean);
          return parts.length > 0 ? parts[0] : '';
        };
        const chosen = src || pickFromSrcset();
        if (chosen && !chosen.startsWith('data:')) {
          try {
            const abs = new URL(chosen, url).href;
            if (!images.includes(abs)) images.push(abs);
          } catch {
            // ignore invalid URLs
          }
        }
      });
    }

    // Get the main image from OpenGraph, Twitter cards, or first valid image
    const ogImg = getMeta('og:image:secure_url') || getMeta('og:image') || getMeta('twitter:image') || getMeta('image') || '';
    const mainImage = ogImg ? new URL(ogImg, url).href : (images.length > 0 ? images[0] : '');

    // Last attempt: use JSON-LD if available
    const fromJsonLd = parseJsonLdProduct();
    if (fromJsonLd) {
      if (!title && fromJsonLd.name) title = fromJsonLd.name as string;
      if (!description && (fromJsonLd as any).description) description = (fromJsonLd as any).description as string;
      if (!price && (fromJsonLd as any).price) price = (fromJsonLd as any).price as number;
      if (!images.length && (fromJsonLd as any).image) images = [new URL((fromJsonLd as any).image as string, url).href];
    }

    // Fallback name from <title> or URL slug
    if (!title) {
      const docTitle = doc.querySelector('title')?.textContent?.trim() || '';
      if (docTitle) {
        title = docTitle;
      } else {
        try {
          const u = new URL(url);
          const parts = u.pathname.split('/').filter(Boolean);
          const last = parts[parts.length - 1] || '';
          if (last) {
            title = decodeURIComponent(last.replace(/[-_]+/g, ' '));
          }
        } catch {
          // ignore URL parse errors
        }
      }
    }

    // If still no image, try common link rels or any non-icon image
    if (!images.length) {
      const linkOg = getMeta('og:image:secure_url') || getMeta('og:image') || getMeta('twitter:image') || '';
      if (linkOg) {
        try { images = [new URL(linkOg, url).href]; } catch { /* noop */ }
      } else {
        const candidates = Array.from(doc.querySelectorAll('img'))
          .map(i => i.getAttribute('src') || i.getAttribute('data-src') || '')
          .filter(Boolean)
          .filter(src => !/sprite|icon|logo|placeholder|svg|\.svg|\.ico/i.test(src));
        if (candidates.length) {
          try { images = [new URL(candidates[0], url).href]; } catch { /* noop */ }
        }
      }
    }

    // Try to extract category from URL or use 'Other' as default
    let category: ProductCategory = 'Other';
    const urlParts = url.split('/');
    if (urlParts.length > 2) {
      const potentialCategory = urlParts[urlParts.length - 2].replace(/[-_]/g, ' ');
      // Check if the extracted category is in our valid categories
      const matchedCategory = PRODUCT_CATEGORIES.find(
        c => c.toLowerCase() === potentialCategory.toLowerCase()
      );
      if (matchedCategory) {
        category = matchedCategory;
      }
    }

    return {
      name: title || '',
      description,
      price,
      originalPrice: price * 1.2, // Add 20% as original price
      image: mainImage,
      images: images.slice(0, 5), // Limit to 5 images
      category,
      isInStock: true,
      stock: 10, // Default stock
      sku: `SKU-${Date.now()}`,
      tags: ['imported']
    };
  };

  const fetchProductFromUrl = async (url: string): Promise<Partial<Product> | null> => {
    if (!url) return null;
    
    setIsLoading(true);
    setError(null);
    console.log('Starting import for URL:', url);

    try {
      let html: string | null = null;

      // Primary: ScraperAPI over HTTPS (prevents mixed-content issues)
      if (USE_SCRAPER_API && SCRAPER_API_KEY) {
        try {
          const apiUrl = `https://api.scraperapi.com?api_key=${SCRAPER_API_KEY}&url=${encodeURIComponent(url)}&render=true`;
          console.log('Using ScraperAPI:', apiUrl);
          const response = await fetch(apiUrl, { method: 'GET' });
          if (!response.ok) {
            throw new Error(`ScraperAPI error: ${response.status} ${response.statusText}`);
          }
          html = await response.text();
        } catch (e) {
          console.warn('ScraperAPI failed, will try AllOrigins fallback', e);
        }
      }

      // Fallback: AllOrigins (CORS proxy)
      if (!html) {
        const response = await fetch(`https://api.allorigins.win/get?url=${encodeURIComponent(url)}`);
        if (!response.ok) {
          throw new Error(`Failed to fetch via fallback: ${response.status} ${response.statusText}`);
        }
        const data = await response.json();
        html = data.contents;
      }
      
      if (!html) {
        throw new Error('No content received from the URL');
      }
      
      console.log('HTML Preview:', html.substring(0, 200) + '...');
      
      // Check for blocking pages
      if (html.includes('captcha') || 
          html.includes('Access Denied') || 
          html.includes('Robot Check') ||
          html.includes('Enter the characters you see below')) {
        throw new Error('Website is blocking automated access. Please try again later or use a different URL.');
      }

      const productData = extractProductData(url, html);
      console.log('Extracted product data:', productData);
      
      // Validate we got some meaningful data (be lenient; allow name OR image)
      if (!productData.name && !productData.image) {
        throw new Error('Could not extract meaningful product data from the page');
      }
      
      return productData;
      
    } catch (err) {
      console.error('Error in fetchProductFromUrl:', err);
      let errorMessage = 'Failed to import product. ';
      
      if (err instanceof Error) {
        errorMessage += err.message;
        
        // Add more user-friendly messages for common errors
        if (err.message.includes('Failed to fetch')) {
          errorMessage = 'Unable to connect to the website. Please check your internet connection and try again.';
        } else if (err.message.includes('blocking')) {
          errorMessage = 'The website is blocking automated access. Please try again later or use a different URL.';
        }
      }
      
      setError(errorMessage);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    fetchProductFromUrl,
    isLoading,
    error,
    resetError: () => setError(null)
  };
};

export default useProductImporter;
